import { useEffect, useState } from 'react';
import { t } from 'i18next';
import {
  useCancelCarePlanGeneratingMutation,
  useGenerateCarePlanMutation,
  usePatientsMetadataQuery,
} from '@common/cache';
import { type Id as ToastId, toast } from 'react-toastify';
import { FormattedToastMessage } from '@components';
import { Box } from '@mui/material';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { components } from '@types';

interface CarePlanDownloadFilters {
  partner_id: string;
  program_name:
    components['schemas']['CarePlanDownloadRequestDto']['program_name'] | '';
  program_status:
    | (
        | components['schemas']['CarePlanDownloadRequestDto']['program_status']
        | 'all_aligned_statuses'
      )
    | '';
  assignment_type: 'me' | 'my_team' | 'all';
  download_format: 'pdf' | 'zip';
  care_notes_range: '90_days' | 'full';
  all_aligned_statuses?: components['schemas']['CarePlanDownloadRequestDto']['all_aligned_statuses'];
}

const initialFilters: CarePlanDownloadFilters = {
  partner_id: '',
  program_name: '',
  program_status: '',
  assignment_type: 'me',
  download_format: 'zip',
  care_notes_range: '90_days',
};

let toastId: ToastId;
export const useCarePlanDownloadModal = ({
  onClose,
  ownPatients,
  teamPatients,
}: {
  onClose: () => void;
  ownPatients: boolean;
  teamPatients: boolean;
}) => {
  const [failureReason, setFailureReason] = useState<null | string>(null);
  const [filters, setFilters] =
    useState<CarePlanDownloadFilters>(initialFilters);

  const { data: metadata } = usePatientsMetadataQuery();

  const { mutateAsync: generateFileMutateAsync, isPending } =
    useGenerateCarePlanMutation();

  const { mutateAsync: cancelGenerateAsync } =
    useCancelCarePlanGeneratingMutation();

  const handleSelectPartner = (event: {
    target: {
      value: string;
    };
  }) => {
    setFilters({
      ...filters,
      partner_id:
        event.target.value === t('common.all') ? '' : event.target.value,
    });
    setFailureReason(null);
  };

  const handleSelectProgramName = (event: {
    target: {
      value: string;
    };
  }) => {
    setFilters({
      ...filters,
      program_name:
        event.target.value === t('common.all')
          ? ''
          : (event.target
              .value as components['schemas']['CarePlanDownloadRequestDto']['program_name']),
    });
    setFailureReason(null);
  };

  const handleSelectProgramStatus = (event: {
    target: {
      value: string;
    };
  }) => {
    setFilters({
      ...filters,
      program_status:
        event.target.value === t('common.all')
          ? ''
          : (event.target
              .value as components['schemas']['CarePlanDownloadRequestDto']['program_status']),
    });
    setFailureReason(null);
  };

  const handleChangeAssignmentType = (value: 'me' | 'my_team' | 'all') => {
    setFilters(prevState => ({ ...prevState, assignment_type: value }));
    setFailureReason(null);
  };

  const handleChangeFormatType = (format: 'zip' | 'pdf') => {
    setFilters(prevState => ({ ...prevState, download_format: format }));
    setFailureReason(null);
  };

  const handleChangeCareNotesRange = (value: '90_days' | 'full') => {
    setFilters(prevState => ({ ...prevState, care_notes_range: value }));
    setFailureReason(null);
  };

  const onCloseAction = () => {
    setFilters({
      ...initialFilters,
      assignment_type:
        !ownPatients && !teamPatients ? 'all' : ownPatients ? 'me' : 'my_team',
    });
    onClose();
    setFailureReason(null);
  };

  const handleGenerateReportCSV = async () => {
    toast.update(toastId, { autoClose: 1 });

    try {
      const response = await generateFileMutateAsync({
        props: {
          assignment_type: filters.assignment_type,
          download_format: filters.download_format,
          all_care_notes: filters.care_notes_range === 'full',
          ...(filters.program_name && { program_name: filters.program_name }),
          ...(filters.partner_id && { partner_id: filters.partner_id }),
          ...(filters.program_status &&
            filters.program_status !== 'all_aligned_statuses' && {
              program_status: filters.program_status,
            }),
          all_aligned_statuses:
            filters.program_status === 'all_aligned_statuses' ? true : false,
        },
      });

      onCloseAction();

      toastId = toast.info(
        <FormattedToastMessage
          title={t(
            `reporting.report.preview.generatingReport.title.${response.status}`,
          )}
          message={t(
            `reporting.report.preview.generatingReport.message.${response.status}`,
          )}
        />,
        {
          closeButton:
            response.status === 201 ? (
              <Box
                onClick={() => cancelGenerateAsync(response.data.id)}
                sx={{ width: '60px' }}
              >
                Cancel
              </Box>
            ) : (
              <></>
            ),
          autoClose: false,
        },
      );
    } catch (error) {
      setFailureReason(formatApiError(error));
    }
  };

  useEffect(() => {
    setFilters(prevState => ({
      ...prevState,
      assignment_type:
        !ownPatients && !teamPatients ? 'all' : ownPatients ? 'me' : 'my_team',
    }));
  }, [ownPatients, teamPatients]);

  return {
    filters,
    handleSelectPartner,
    handleSelectProgramName,
    handleSelectProgramStatus,
    handleChangeAssignmentType,
    handleChangeFormatType,
    handleChangeCareNotesRange,
    programNameOptions: (metadata?.program_options ?? []).map(o => ({
      value: o,
      label: o,
    })),
    programStatusOptions: (
      metadata?.program_status_options[filters.program_name || 'All'] ?? []
    ).map(o => ({
      value: o,
      label: o,
    })),
    partnersOptions: (metadata?.partners ?? []).map(o => ({
      value: o.id,
      label: o.name,
    })),
    handleGenerateReportCSV,
    isPending,
    onCloseAction,
    failureReason,
  };
};
